/**
 * AssessmentManagerModal — "Manage Questions" entry point for the pipeline's
 * mcq/coding/both stage. The pipeline job itself can never directly host an
 * MCQ/Coding round (its eventType is always INTERVIEW, and the frozen
 * candidate-facing GET .../mcq route hard-requires eventType === "MCQ") —
 * so on first open this transparently provisions a separate MCQ or CODING
 * job behind the scenes (pipeline_service.create_assessment_job) and then
 * mounts the exact same, unmodified QuestionBankManager UI recruiters
 * already use for standalone MCQ/Coding jobs, pointed at that job's id.
 * Nothing about the underlying engine changes — this only automates the
 * "create a separate job for this" step so it never leaves the pipeline
 * dashboard.
 */
import React, { useEffect, useState } from "react";
import { getPipelineAssessment, createPipelineAssessment } from "../services/jobService";
import QuestionBankManager from "./QuestionBankManager";
import "./RecruiterJobs.css";
import "./QuestionBankManager.css";

const KIND_TO_EVENT_TYPE = { mcq: "MCQ", coding: "CODING", both: "MCQ" };
const KIND_LABELS = { mcq: "MCQ", coding: "Coding", both: "MCQ + Coding" };

// Builds the "Experience" prefill text from resumeCriteria's numeric range
// (min/max_experience_years, from the JD-extraction schema) — best-effort,
// blank if neither bound was ever entered.
function experienceTextFromCriteria(criteria) {
  const min = criteria?.min_experience_years;
  const max = criteria?.max_experience_years;
  if (min != null && max != null) return `${min}-${max} years`;
  if (min != null) return `${min}+ years`;
  if (max != null) return `Up to ${max} years`;
  return "";
}

// Builds a readable JD-like text block from resumeCriteria for the "Generate
// with AI" JD field — resumeCriteria stores structured fields (no raw JD
// text is persisted), so this reassembles the most JD-relevant parts back
// into prose the question generator already knows how to read. Exported so
// InterviewQuestionsModal.jsx can reuse the exact same reconstruction for
// the AI Interview stage's question generator.
export function jdTextFromCriteria(criteria) {
  if (!criteria) return "";
  const parts = [];
  if (criteria.role_summary) parts.push(criteria.role_summary);
  if (criteria.key_responsibilities?.length) {
    parts.push("Key responsibilities:\n" + criteria.key_responsibilities.map((r) => `- ${r}`).join("\n"));
  }
  if (criteria.mandatory_requirements?.length) {
    parts.push("Mandatory requirements:\n" + criteria.mandatory_requirements.map((r) => `- ${r}`).join("\n"));
  }
  if (criteria.required_skills?.length) parts.push("Required skills: " + criteria.required_skills.join(", "));
  if (criteria.preferred_skills?.length) parts.push("Preferred skills: " + criteria.preferred_skills.join(", "));
  return parts.join("\n\n");
}

export default function AssessmentManagerModal({ open, jobId, kind, companyName, jobTitle, resumeCriteria, onClose }) {
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !jobId || !kind) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        let existing = await getPipelineAssessment(jobId);
        if (!existing) existing = await createPipelineAssessment(jobId, kind);
        if (!cancelled) setAssessment(existing);
      } catch (err) {
        if (!cancelled) setError(err.message || "Unable to set up the assessment");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, jobId, kind]);

  if (!open) return null;

  return (
    <div className="recruiter-modal-backdrop" role="presentation" onClick={onClose}>
      <div className="recruiter-modal recruiter-modal--wide" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="recruiter-modal-header">
          <div>
            <h2>{jobTitle || "Assessment"} — Questions</h2>
            <p>{KIND_LABELS[kind] || kind} round for this pipeline</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close">x</button>
        </div>

        {loading ? (
          <div className="recruiter-empty">Setting up the assessment…</div>
        ) : error ? (
          <div className="job-form"><div className="job-form-error">{error}</div></div>
        ) : (
          <QuestionBankManager
            jobId={assessment.assessmentJobId}
            eventType={KIND_TO_EVENT_TYPE[assessment.kind]}
            includeCoding={assessment.kind === "both"}
            companyName={companyName}
            onClose={onClose}
            initialRole={resumeCriteria?.job_title || jobTitle || ""}
            initialExperience={experienceTextFromCriteria(resumeCriteria)}
            initialJdText={jdTextFromCriteria(resumeCriteria)}
          />
        )}
      </div>
    </div>
  );
}
